import { useId, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { introCardCopy } from "./introCardCopy";

/**
 * D7 — "What went out", folded to its first three lines with Show full message /
 * Show less. The full text is always in the DOM (a clamp, not a cut), so what
 * the recipient got is never more than one click away.
 */
export function SentFold({
  body,
  testId,
  children,
}: {
  body: string;
  testId: string;
  /** Notes that belong under the message (the drift note, the combined line). */
  children?: ReactNode;
}) {
  const [full, setFull] = useState(false);
  const id = useId();
  return (
    <div className="grid gap-space-2" data-testid={testId}>
      <span className="text-small font-medium">{introCardCopy.whatWentOut}</span>
      <div
        id={id}
        className={cn(
          "rounded-md border border-border-light bg-white-warm px-space-3 py-space-2",
          full && "max-h-80 overflow-y-auto",
        )}
      >
        <p
          className={cn("text-small text-onyx whitespace-pre-wrap m-0", !full && "line-clamp-3")}
          data-testid={`${testId}-body`}
          data-folded={full ? "false" : "true"}
        >
          {body}
        </p>
      </div>
      <div>
        <Button
          variant="text"
          size="sm"
          aria-expanded={full}
          aria-controls={id}
          onClick={() => setFull((v) => !v)}
          data-testid={`${testId}-toggle`}
        >
          {full ? introCardCopy.showLess : introCardCopy.showFullMessage}
        </Button>
      </div>
      {children}
    </div>
  );
}

/** The one control that reveals a sent card's editor (J8). */
export function EditAndSendAgain({ onClick, testId }: { onClick: () => void; testId: string }) {
  return (
    <div>
      <Button variant="secondary" size="sm" onClick={onClick} data-testid={testId}>
        {introCardCopy.editAndSendAgain}
      </Button>
    </div>
  );
}

/** D5 — one small text control at a tab's top right. "Collapse all" while any
 *  card is open, "Expand all" once none is. */
export function ExpandAllControl({
  anyOpen,
  onExpandAll,
  onCollapseAll,
  testId,
}: {
  anyOpen: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  testId: string;
}) {
  return (
    <div className="flex justify-end">
      <Button
        variant="text"
        size="sm"
        onClick={anyOpen ? onCollapseAll : onExpandAll}
        data-testid={testId}
      >
        {anyOpen ? introCardCopy.collapseAll : introCardCopy.expandAll}
      </Button>
    </div>
  );
}
